'use client'

import { useState, useEffect } from 'react'
import { createClient } from '@/lib/supabase/client'
import { useRouter, useSearchParams } from 'next/navigation'
import Link from 'next/link'
import HeroBackgroundDynamic from '@/components/marketing/HeroBackgroundDynamic'
import { SignupLeftPaneContent } from '@/components/auth/SignupLeftPaneContent'
import { humanizeAuthError, getPasswordStrength } from '@/lib/auth-errors'
import { trainer } from '@chalk/shared/tokens'
import { FEES } from '@chalk/shared/pricing'
import posthog from 'posthog-js'

type Step = 'age' | 'account'

export default function SignupPage() {
  const supabase = createClient()
  const router   = useRouter()

  // Age gate — C1/C2 COPPA compliance
  const [ageBlocked,  setAgeBlocked]  = useState(false)

  const [step,     setStep]     = useState<Step>('age')
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')

  // Sprint 61.5 — trainer-flavored signup framing.
  // Sprint 61.6 refactor: switched from a useState+useEffect pattern to
  // useSearchParams() so the badge renders on first hydration paint (no
  // flicker from generic → trainer framing as React hydrates). The fresh-
  // signup e2e test missed the framing because it screenshotted before the
  // useEffect fired; useSearchParams resolves synchronously at hydration.
  const searchParams = useSearchParams()
  const isTrainerSignup = searchParams?.get('role') === 'trainer'

  // Sprint 61.6 — also pre-set the chalk_redirect_intent cookie at mount so
  // that the email-confirmation auth flow (which round-trips through a fresh
  // tab/session) preserves the trainer routing intent. Without this, a user
  // who confirms via email link lands at /api/auth/callback?redirect=app →
  // /dashboard → /onboarding (because they have is_trainer=false until handle
  // claim). Setting the cookie at mount means the callback sees /train/setup
  // intent and routes correctly.
  useEffect(() => {
    if (!isTrainerSignup) return
    document.cookie = `chalk_redirect_intent=${encodeURIComponent('/train/setup')}; path=/; max-age=3600; SameSite=Lax`
  }, [isTrainerSignup])

  // Cycle-9 analytics close (2026-05-14): emit `signup_started` on mount so
  // the acquisition funnel can count "landed on /signup → completed signup"
  // as its top step. Pre-fix the funnel started at `signup_completed`, which
  // hid drop-off between landing and conversion entirely. The intent param
  // splits trainer-flavored signup from generic coach/parent signup.
  useEffect(() => {
    posthog.capture('signup_started', {
      platform: 'web',
      intent: isTrainerSignup ? 'trainer' : 'generic',
    })
    // Run once on mount — re-emitting on isTrainerSignup change would double-
    // count if the user navigates between /signup and /signup?role=trainer.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ToS acceptance — H1 compliance
  const [tosAccepted, setTosAccepted] = useState(false)

  const [email,    setEmail]    = useState('')
  const [password, setPassword] = useState('')

  // ── Compute post-auth destination from URL params ─────────────────────────

  function getPostAuthUrl(): string {
    const p = new URLSearchParams(window.location.search)
    const invite    = p.get('invite')
    const fanInvite = p.get('fan_invite')
    const redirect  = p.get('redirect')
    const role      = p.get('role')
    if (invite)    return `/teams/invite/${invite}`
    if (fanInvite) return `/teams/invite/fan/${fanInvite}`
    // Sprint 65.1 hardening — CRITICAL fix for open-redirect vulnerability.
    // Previous: `if (redirect) return redirect` accepted any URL including
    // absolute origins like https://evil.com, which a phishing link could use
    // to land users on an attacker site after auth. Sprint 61's /for-trainers
    // increased exposure of /signup, so this needed locking down. Only accept
    // same-origin relative paths (starts with single slash, not protocol-
    // relative double-slash).
    if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
      return redirect
    }
    // Sprint 61 — trainer-flavored signup. /for-trainers and any other trainer
    // entry point links to /signup?role=trainer; after auth we route to the
    // handle-claim page instead of generic /onboarding. Market-analyst verdict:
    // keep a single signup route and branch by query param rather than forking
    // into /trainers/signup, which keeps the DB-write path unified.
    if (role === 'trainer') return '/train/setup'
    return '/onboarding'
  }

  // ── Age gate ─────────────────────────────────────────────────────────────────

  function handleAgeUnder13() {
    // Do NOT collect any data or create any account
    setAgeBlocked(true)
  }

  function handleAge13Plus() {
    setStep('account')
  }

  // ── Account creation ─────────────────────────────────────────────────────────

  async function handleAccount(e: React.FormEvent) {
    e.preventDefault()
    if (!tosAccepted) return
    setLoading(true)
    setError('')
    try {
      // Set intent cookie before signup so the email-confirmation callback can
      // redirect back to the invite page even if the tab/browser is closed and
      // reopened — matches the same pattern as handleOAuth.
      const dest = getPostAuthUrl()
      if (dest !== '/onboarding') {
        document.cookie = `chalk_redirect_intent=${encodeURIComponent(dest)}; path=/; max-age=3600; SameSite=Lax`
      }
      const { data, error: err } = await supabase.auth.signUp({
        email, password,
        options: { emailRedirectTo: `${window.location.origin}/api/auth/callback?redirect=app` },
      })
      if (err) throw err

      // Record ToS acceptance on the profile (H1 compliance)
      if (data?.user) {
        await supabase.from('profiles').upsert({
          id:              data.user.id,
          tos_accepted_at: new Date().toISOString(),
          tos_version:     '2026-04',
        } as any)
        posthog.capture('signup_completed', { method: 'email', platform: 'web' })
      }

      router.push(getPostAuthUrl())
    } catch (err: any) {
      setError(humanizeAuthError(err.message))
    } finally {
      setLoading(false)
    }
  }

  async function handleOAuth(provider: 'google' | 'apple') {
    posthog.capture('signup_initiated', { method: provider, platform: 'web' })
    const dest = getPostAuthUrl()
    // Store non-default destinations so the callback can redirect there after OAuth.
    // Sprint 61.7 — extended max-age from 300s (5min) to 3600s (1h) to match
    // email signup. Google OAuth round-trip (sign in → confirm → consent → 2FA)
    // can exceed 5 minutes for users on slow networks or 2FA-enabled accounts,
    // and expiring cookie was dropping trainers back to /onboarding's coach
    // picker (which has no trainer option).
    if (dest !== '/onboarding') {
      document.cookie = `chalk_redirect_intent=${encodeURIComponent(dest)}; path=/; max-age=3600; SameSite=Lax`
    }
    await supabase.auth.signInWithOAuth({
      provider,
      options: { redirectTo: `${window.location.origin}/api/auth/callback?redirect=app` },
    })
  }

  // ── Under-13 blocked screen ───────────────────────────────────────────────────
  if (ageBlocked) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center px-8 text-center max-w-sm mx-auto" style={{ backgroundColor: '#FAFAF8' }}>
        <div
          className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
          style={{ background: '#FEFCE8' }}
        >
          <span className="text-3xl" aria-hidden="true">🧢</span>
        </div>
        <h1 className="text-xl font-black tracking-tight mb-3" style={{ color: '#1A1814' }}>
          Chalk is for coaches, parents,<br />and players 13 and older.
        </h1>
        <p className="text-sm leading-relaxed mb-8" style={{ color: '#3D3B35' }}>
          If you&apos;re under 13, ask your parent or guardian to
          create an account and add you to the team.
        </p>
        <a
          href="/"
          className="text-sm font-semibold transition-colors hover:opacity-80"
          style={{ color: '#6C6961' }}
        >
          ← Back to home
        </a>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex">
      {/* Sprint 61.12 — persona-aware focus ring via CSS custom properties.
       *  --focus-border + --focus-ring are set on the <form> below; this
       *  style block consumes them in the :focus pseudo-class. Trainer route
       *  gets violet, default gets navy. Inline <style> is fine here because
       *  the entire route is 'use client'. */}
      <style>{`
        /* Sprint 61.14 — 2-layer focus ring for WCAG 2.2 AA SC 1.4.11 / 2.4.11.
         *  Previous single-layer rgba @ 0.20 composited to 1.18:1 against the
         *  page bg — failed the 3:1 non-text contrast minimum. The 2-layer
         *  pattern now uses a solid 2px outline (6.8:1 for #6D28D9 violet on
         *  #FAFAF8, 16.96:1 for #0f172a navy) as the WCAG-conformant
         *  indicator. The translucent box-shadow remains as decorative glow. */
        .signup-input:focus {
          outline:        2px solid var(--focus-border, #0f172a);
          outline-offset: 2px;
          border-color:   var(--focus-border, #0f172a) !important;
          box-shadow:     0 0 0 4px var(--focus-ring, rgba(15,23,42,0.15));
        }
        /* Custom :focus-visible behavior so the solid outline only appears on
         *  keyboard focus, not on mouse click — purely visual polish. The
         *  box-shadow halo is suppressed in the mouse-focus case to keep the
         *  click experience clean while preserving keyboard a11y. */
        .signup-input:focus:not(:focus-visible) { outline: none; box-shadow: none; }
        .signup-input::placeholder { color: #A8A39B; }
        /* Sprint 61.12 — defined brand hover (#F5E642) instead of opacity-90 sheen
         *  which desaturates yellow toward beige. */
        .signup-cta:not(:disabled):hover { background-color: #F5E642 !important; }
      `}</style>

      {/* Left panel — dark navy (matches /for-trainers + Sidebar). Sprint 61.12:
       *  unified to #0f172a; previously #0C0E14 was a third off-system shade
       *  that broke the marketing→auth color continuity.
       *
       *  Sprint 61.20 — design-intelligence flagged the panel as "720px of
       *  near-empty navy" the user stares at while signing up. SignupLeftPane
       *  Content now layers above the HeroBackgroundDynamic with either:
       *    - Trainer route: mock booking page preview (the GTM payoff)
       *    - Default route: testimonial + trust strip (social proof at the
       *      decision moment)
       *
       *  HeroBackgroundDynamic stays as the animated decorative base; content
       *  sits in z-10 above it. */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden" style={{ background: '#0f172a' }}>
        <HeroBackgroundDynamic />
        <SignupLeftPaneContent isTrainer={isTrainerSignup} />
      </div>

      {/* Right panel — warm light (Q7 authenticated palette). Sprint 61.12:
       *  swapped from bg-white to #FAFAF8 so signup is already on the same
       *  warm surface the authenticated app uses. Bridges marketing→app. */}
      <div className="flex-1 lg:w-1/2 flex flex-col min-h-screen" style={{ backgroundColor: '#FAFAF8' }}>

        {/* Sprint 61.12 — trainer persona banner. Spans the full width of the
         *  right panel above the top bar so any trainer arriving from
         *  /for-trainers gets one unambiguous "you're in the right place" cue
         *  above the fold. */}
        {isTrainerSignup && (
          <div
            className="px-8 sm:px-12 lg:px-16 py-2 text-center text-[11px] font-bold uppercase tracking-wider"
            style={{
              backgroundColor: trainer.tint,
              borderBottom:    `1px solid ${trainer.tintBorder}`,
              color:           trainer.chip.textLight,
            }}
          >
            <span aria-hidden="true">●</span> Setting up your trainer account
          </div>
        )}

        {/* Top bar */}
        <div className="flex items-center justify-between px-8 sm:px-12 lg:px-16 py-6 shrink-0">
          <a href="/" className="text-[22px] font-bold leading-none" style={{ fontFamily: 'var(--font-caveat)', color: '#1A1814' }}>
            chalk<span style={{ color: '#F5E642' }}>.</span>
          </a>
          <a href="/" className="text-sm transition-colors hover:opacity-80" style={{ color: '#6C6961' }}>
            Back to site
          </a>
        </div>

        {/* Form area — Sprint 61.23 UX F2.2: max-w-md (448px) clamped the H1
         *  too tight, wrapping "Start your trainer account" at Lora-equivalent
         *  56px to 3 lines. Bumped desktop to max-w-xl (576px) so the longest
         *  H1 fits in 1-2 lines; smaller breakpoints retain tighter clamp. */}
        <div className="flex-1 flex flex-col justify-center px-8 sm:px-12 lg:px-16 py-10 max-w-md lg:max-w-xl w-full mx-auto">

          {/* ── Step: Age verification ──────────────────────────── */}
          {step === 'age' && (
            <>
              {/* Sprint 61.12 — trainer pill removed. Persona signal now lives
               *  in the full-width banner at the top of the right panel. */}
              <div className="mb-8">
                {/* Sprint 61.17 — triple-scale headline per design-intelligence
                 *  recommendation. Previously text-2xl (24px) read as "in beta"
                 *  against the 720px near-empty left pane. Cash App / Linear /
                 *  Apple ID render comparable signup H1s at 40–64px. Responsive
                 *  scale: 32px mobile → 48px tablet → 56px desktop. */}
                {/* Sprint 61.24 UX F5.3 — Lora carried through from /for-
                 *  trainers to /signup for continuous trainer-brand identity. */}
                <h1 className="text-[2rem] sm:text-5xl lg:text-[3.5rem] tracking-tight leading-[1.05] mb-2" style={{ color: '#1A1814', fontFamily: 'var(--font-lora), Georgia, serif', fontWeight: 700, letterSpacing: '-0.02em' }}>
                  {isTrainerSignup ? 'Start your trainer account' : 'How old are you?'}
                </h1>
                <p className="text-sm" style={{ color: '#3D3B35' }}>
                  {isTrainerSignup
                    ? 'First — confirm you\'re 13 or older. Then we\'ll set up your booking link.'
                    : 'Chalk is for coaches, parents, and players 13 and older.'}
                </p>
              </div>

              {/* Sprint 61.29 UX F2.4 — age-gate buttons now visually
               *  differentiated. Previous: two identical white-card-with-
               *  border tiles read as form inputs, not choices. New:
               *  "13 or older" gets persona-tinted primary treatment
               *  (violet outline + tint bg on trainer route, navy on
               *  default) so the expected path is visually obvious;
               *  "Under 13" stays subordinate as a ghost card. */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={handleAge13Plus}
                  className="flex items-center gap-4 w-full text-left rounded-xl px-5 py-4 active:scale-[0.99] transition-all group"
                  style={{
                    backgroundColor: isTrainerSignup ? trainer.tint : 'rgba(15,23,42,0.04)',
                    border:          `1.5px solid ${isTrainerSignup ? trainer.tintBorderStrong : '#0f172a'}`,
                  }}
                >
                  <div className="flex-1">
                    <p className="text-sm font-bold" style={{ color: '#1A1814' }}>13 or older</p>
                    <p className="text-xs mt-0.5" style={{ color: '#3D3B35' }}>I am at least 13 years old</p>
                  </div>
                  <svg className="w-4 h-4 transition-colors shrink-0" style={{ color: isTrainerSignup ? trainer.primaryDark : '#0f172a' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </button>

                <button
                  onClick={handleAgeUnder13}
                  className="flex items-center gap-4 w-full text-left rounded-xl px-5 py-4 active:scale-[0.99] transition-all group"
                  style={{
                    backgroundColor: 'transparent',
                    border:          '1px solid #E8E6E0',
                  }}
                >
                  <div className="flex-1">
                    <p className="text-sm font-bold" style={{ color: '#3D3B35' }}>Under 13</p>
                    <p className="text-xs mt-0.5" style={{ color: '#6C6961' }}>I am younger than 13 years old</p>
                  </div>
                  <svg className="w-4 h-4 transition-colors shrink-0" style={{ color: '#A8A39B' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    <path d="M9 18l6-6-6-6"/>
                  </svg>
                </button>
              </div>

              <p className="text-center text-xs mt-6" style={{ color: '#6C6961' }}>
                Already have an account?{' '}
                <Link href="/login" className="hover:underline font-semibold" style={{ color: isTrainerSignup ? trainer.primaryDark : '#0f172a' }}>
                  Sign in
                </Link>
              </p>
            </>
          )}

          {/* ── Step: Account ───────────────────────────────────── */}
          {step === 'account' && (
            <>
              <div className="mb-8">
                {/* Sprint 61.17 — triple-scale headline per design-intelligence
                 *  recommendation. Previously text-2xl (24px) read as "in beta"
                 *  against the 720px near-empty left pane. Cash App / Linear /
                 *  Apple ID render comparable signup H1s at 40–64px. Responsive
                 *  scale: 32px mobile → 48px tablet → 56px desktop. */}
                {/* Sprint 61.24 UX F5.3 — Lora carried through from /for-
                 *  trainers to /signup for continuous trainer-brand identity. */}
                <h1 className="text-[2rem] sm:text-5xl lg:text-[3.5rem] tracking-tight leading-[1.05] mb-2" style={{ color: '#1A1814', fontFamily: 'var(--font-lora), Georgia, serif', fontWeight: 700, letterSpacing: '-0.02em' }}>
                  Create your account
                </h1>
                {/* 2026-05-14 — disclosure bound to FEES.trainerPlatformDisplay
                 *  (runtime PLATFORM_FEE_RATE = 10% in trainer book + package
                 *  routes). Previous "1% per session" copy was a misleading
                 *  understatement vs. the actual deduction; flagged by the
                 *  monetization audit as a legal-exposure surface. */}
                <p className="text-sm" style={{ color: '#3D3B35' }}>
                  {isTrainerSignup
                    ? `Free to start. ${FEES.trainerPlatformDisplay} platform fee per session. No monthly subscription.`
                    : 'Free forever for coaches. No credit card required.'}
                </p>
              </div>

              <div className="flex flex-col gap-3 mb-5">
                <button
                  onClick={() => handleOAuth('google')}
                  className="w-full flex items-center justify-center gap-3 font-semibold text-sm rounded-xl py-3.5 active:scale-[0.99] transition-all shadow-sm"
                  style={{
                    minHeight: '48px',
                    backgroundColor: '#FFFFFF',
                    border:          '1px solid #E8E6E0',
                    color:           '#1A1814',
                  }}
                >
                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </button>

                <button
                  onClick={() => handleOAuth('apple')}
                  className="w-full flex items-center justify-center gap-3 font-semibold text-sm rounded-xl py-3.5 active:scale-[0.99] transition-all shadow-sm"
                  style={{
                    minHeight: '48px',
                    backgroundColor: '#FFFFFF',
                    border:          '1px solid #E8E6E0',
                    color:           '#1A1814',
                  }}
                >
                  <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.54 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09z"/>
                    <path d="M15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.56-1.701z"/>
                  </svg>
                  Continue with Apple
                </button>
              </div>

              <div className="relative flex items-center mb-5">
                <div className="flex-1 h-px" style={{ backgroundColor: '#E8E6E0' }} />
                <span className="mx-3 text-xs" style={{ color: '#6C6961' }}>or</span>
                <div className="flex-1 h-px" style={{ backgroundColor: '#E8E6E0' }} />
              </div>

              {/* Sprint 61.12 — persona-tinted focus ring via inline style.
               *  Trainer route: violet rgba(109,40,217,0.20). Default: navy.
               *  CSS variable trick: set --focus-color on the parent, consumed
               *  in the focus-within ring via inline style on each input.  */}
              <form
                onSubmit={handleAccount}
                className="space-y-3"
                style={{
                  ['--focus-ring' as string]: isTrainerSignup ? trainer.focusRing : 'rgba(15,23,42,0.15)',
                  ['--focus-border' as string]: isTrainerSignup ? trainer.focusBorder : '#0f172a',
                }}
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Email address"
                  autoComplete="email"
                  enterKeyHint="next"
                  className="signup-input w-full rounded-xl px-4 py-3.5 text-sm focus:outline-none transition-all"
                  style={{
                    backgroundColor: '#FFFFFF',
                    border:          '1px solid #E8E6E0',
                    color:           '#1A1814',
                  }}
                />
                <div>
                  <input
                    type="password"
                    required
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    placeholder="Password (8+ characters)"
                    autoComplete="new-password"
                    enterKeyHint="done"
                    className="signup-input w-full rounded-xl px-4 py-3.5 text-sm focus:outline-none transition-all"
                    style={{
                      backgroundColor: '#FFFFFF',
                      border:          '1px solid #E8E6E0',
                      color:           '#1A1814',
                    }}
                  />
                  {password && (() => {
                    const s = getPasswordStrength(password)
                    // Sprint 61.12 — semantic palette for password strength:
                    //   strong → emerald-600 (#059669) for AA contrast on light
                    //   fair   → warning amber-700 (#D97706); previous #FBBF24 is reserved for sport identity
                    //   weak   → red-600 (#DC2626)
                    const cfg = s === 'strong'
                      ? { w: 'w-full', color: '#059669', label: 'Strong' }
                      : s === 'fair'
                      ? { w: 'w-2/3',  color: '#D97706', label: 'Fair'   }
                      : { w: 'w-1/3',  color: '#DC2626', label: 'Weak'   }
                    return (
                      <div className="mt-1.5 flex items-center gap-2">
                        <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ backgroundColor: '#F5F4F0' }}>
                          <div className={`h-full rounded-full transition-all duration-300 ${cfg.w}`} style={{ backgroundColor: cfg.color }} />
                        </div>
                        <span className="text-xs w-10 text-right" style={{ color: '#6C6961' }}>{cfg.label}</span>
                      </div>
                    )
                  })()}
                </div>

                {/* ToS acceptance — H1 compliance */}
                <label className="flex items-start gap-3 cursor-pointer py-1">
                  <input
                    type="checkbox"
                    checked={tosAccepted}
                    onChange={e => setTosAccepted(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded shrink-0"
                    style={{
                      borderColor: '#E8E6E0',
                      accentColor: isTrainerSignup ? trainer.primary : '#0f172a',
                    }}
                  />
                  <span className="text-xs leading-relaxed" style={{ color: '#3D3B35' }}>
                    I agree to the{' '}
                    <a
                      href="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2"
                      style={{ color: isTrainerSignup ? trainer.primaryDark : '#0f172a' }}
                    >
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a
                      href="/privacy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="underline underline-offset-2"
                      style={{ color: isTrainerSignup ? trainer.primaryDark : '#0f172a' }}
                    >
                      Privacy Policy
                    </a>
                  </span>
                </label>

                {error && (
                  <div className="rounded-xl px-4 py-3" style={{ backgroundColor: 'rgba(220,38,38,0.06)', border: '1px solid rgba(220,38,38,0.20)' }}>
                    <p className="text-xs" style={{ color: '#DC2626' }}>
                      {error}
                      {error.includes('already in use') && (
                        <> <a href="/login/password" className="underline font-medium">Sign in →</a></>
                      )}
                    </p>
                  </div>
                )}
                {/* Sprint 61.30 UX F2.5 — disabled CTA now persona-tinted.
                 *  Sprint 61.15's muted-grey disabled state still read as
                 *  broken-looking. Now: trainer route gets a soft violet
                 *  tint with violet-700 text (still 6.16:1 on the tint);
                 *  default route gets a soft navy tint with navy text.
                 *  Clearly signals "this WILL be the action button once
                 *  you finish" rather than "this button is dead." */}
                <button
                  type="submit"
                  disabled={loading || !email || !password || !tosAccepted}
                  className="signup-cta w-full font-black text-sm rounded-xl py-3.5 active:scale-[0.99] transition-all disabled:cursor-not-allowed"
                  style={{
                    backgroundColor: (loading || !email || !password || !tosAccepted)
                      ? (isTrainerSignup ? 'rgba(139,92,246,0.10)' : 'rgba(15,23,42,0.06)')
                      : '#F5E642',
                    color: (loading || !email || !password || !tosAccepted)
                      ? (isTrainerSignup ? trainer.primaryDark : '#0f172a')
                      : '#0A0F1A',
                  }}
                >
                  {loading ? 'Creating account...' : 'Continue with email'}
                </button>
                {/* Sprint 61.15 — explicit "why is this disabled" cue when ToS
                 *  is the only remaining blocker. Without this hint, users who
                 *  filled email + password but didn't see the ToS checkbox
                 *  read the disabled CTA as broken (a documented ux-designer
                 *  finding from the live walkthrough). Rendered only when the
                 *  ToS is the specific blocker — not for empty form. */}
                {email && password && !tosAccepted && !loading && (
                  <p className="text-center text-xs -mt-1" style={{ color: '#6C6961' }}>
                    <span aria-hidden="true">↑</span> Accept the Terms of Service to continue
                  </p>
                )}
              </form>

              <p className="text-center text-xs mt-6" style={{ color: '#6C6961' }}>
                Already have an account?{' '}
                <Link
                  href="/login"
                  className="hover:underline font-semibold"
                  style={{ color: isTrainerSignup ? trainer.primaryDark : '#0f172a' }}
                >
                  Sign in
                </Link>
              </p>
            </>
          )}

        </div>
      </div>
    </div>
  )
}
